import { useNavigate } from 'react-router-dom';
import {
  ATable,
  DropdownOptions,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
  StatusActive,
} from '../../components';
import { useToast } from '../../hooks/toast';
import { useCallback, useEffect, useState } from 'react';
import { RiHealthBookLine } from 'react-icons/ri';
import {
  EditOutlined,
  PlusCircleOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { MenuProps } from 'antd';
import api from '../../services/api';
import { IModelForm } from '../../shared/interfaces';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';
import { useI18n } from '../../shared/i18n';

const Forms: React.FC = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { permissionFind } = useAuth();
  const { t } = useI18n();
  const [search, setSearch] = useState<string>('');
  const [forms, setForms] = useState<IModelForm[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const listForms = useCallback(async () => {
    setLoading(true);
    setForms([]);
    try {
      const response = await api.post('/forms/list', {
        take: limit,
        skip: (page - 1) * limit,
        keyword: search,
      });
      setPages(response.data.count);
      setForms(response.data.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: t('modelsFormsPage.toast.errorTitle'),
        description: t('modelsFormsPage.toast.listError'),
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast, t]);

  const renderMenuOptions = () => {
    const options: MenuProps['items'] = [];
    options.push({
      label: t('modelsFormsPage.actions.refresh'),
      key: '1',
      icon: <RedoOutlined />,
      onClick: () => listForms(),
    });
    if (permissionFind([PermissionActions.modelFormsCreate])) {
      options.push({
        label: t('modelsFormsPage.actions.create'),
        key: '2',
        icon: <PlusCircleOutlined />,
        onClick: () => navigate('/model_form/new'),
      });
    }

    return options;
  };

  useEffect(() => {
    listForms();
  }, [page, search, limit, listForms]);

  return (
    <PageLayout>
      <PageIntro
        badge={t('modelsFormsPage.intro.badge')}
        icon={<RiHealthBookLine />}
        title={t('modelsFormsPage.intro.title')}
        description={t('modelsFormsPage.intro.description')}
      />
      <MenuOptions
        menu_items={renderMenuOptions()}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        <ATable
          scroll={{ y: 510 }}
          columns={[
            {
              title: t('modelsFormsPage.table.title'),
              dataIndex: 'title',
            },
            {
              title: t('modelsFormsPage.table.status'),
              dataIndex: 'status',
              width: 95,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: t('modelsFormsPage.table.options'),
              width: 130,
              render: (cell: IModelForm) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.modelFormsUpdate])
                      ? {
                          label: t('modelsFormsPage.actions.edit'),
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => navigate(`/model_form/${cell.id}`),
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={forms}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
    </PageLayout>
  );
};

export default Forms;
